"use client";

// OptionsMenu — the document "options" surface, opened on LEFT-click of the bar
// (not right-click). On desktop it's the shared frosted ContextMenu anchored to
// the trigger; on phones it becomes THE sheet (rising from the bottom, headed by
// the name of the thing the options are about) with the same items, so the
// interaction is consistent everywhere. Copy lives in the share sheet, per spec —
// this is for actions on the document itself (rename now; more later).
//
// The sheet stays mounted for its way down (EtherModal plays the exit), and it
// can be opened from inside another sheet (a share bar in a sheet's foot): both
// are EtherModals, and the later one stands on top.

import { useEffect, useState, type ReactNode } from "react";
import ContextMenu, { type MenuItem } from "../ContextMenu";
import Sheet from "../Sheet";

export default function OptionsMenu({
  open,
  onClose,
  items,
  anchor,
  title = "options",
  footer,
}: {
  open: boolean;
  onClose: () => void;
  items: MenuItem[];
  /** trigger's bounding-rect edges — `bottom` lets ContextMenu decide whether to
   *  open below or flip upward against the ACTUAL button, not a single point. */
  anchor: { x: number; y: number; bottom?: number } | null;
  title?: string;
  /** optional row after the items — desktop popover AND mobile sheet (e.g. the
   *  undo/redo pair). Its buttons don't auto-close, so actions can chain. */
  footer?: ReactNode;
}) {
  const [mobile, setMobile] = useState(false);
  // what the sheet shows while it sinks: the title and rows it had when it was
  // asked to close (the caller may clear them the same instant)
  const [held, setHeld] = useState({ title, items });
  if (open && (held.title !== title || held.items !== items)) setHeld({ title, items });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 719px)");
    const on = () => setMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  if (mobile) {
    return (
      <Sheet
        open={open}
        onClose={onClose}
        labelledBy="art-options-title"
        title={held.title}
        footStart={footer ? <div className="art-sheet__foot">{footer}</div> : undefined}
      >
        <div className="art-sheet__body">
          {held.items.map((it, i) => (
            <div key={i} className="art-sheet__item">
              {it.separated && i > 0 ? <div className="art-sheet__rule" aria-hidden /> : null}
              <button
                type="button"
                className={`art-sheet__row${it.danger ? " art-sheet__row--danger" : ""}`}
                disabled={it.disabled}
                onClick={() => {
                  it.onSelect?.();
                  onClose();
                }}
              >
                <span className="art-sheet__ico" aria-hidden>{it.icon}</span>
                <span className="art-sheet__label">{it.label}</span>
                {it.hint && <span className="art-sheet__hint">{it.hint}</span>}
              </button>
            </div>
          ))}
        </div>
      </Sheet>
    );
  }

  if (!open) return null;

  return (
    <ContextMenu
      menu={anchor ? { x: anchor.x, y: anchor.y, items, anchorTop: anchor.y, anchorBottom: anchor.bottom ?? anchor.y } : null}
      onClose={onClose}
      footer={footer}
    />
  );
}
